import { Link } from "react-router-dom";
import { Eye, FlaskConical, ShieldCheck } from "lucide-react";
import type { B2bWriteModes } from "../../../../../../shared/index";

/**
 * States, at the top of every B2B page, what the section is currently allowed to do.
 *
 * `ExternalApiDB` is production-only, so operations are promoted from `readonly` to
 * `dry-run` to `live` one at a time. Without this banner an operator cannot tell
 * whether the change they just made actually happened — which is exactly the mistake
 * a staged rollout invites.
 */
export function WriteModeBanner({ modes }: { modes: B2bWriteModes }) {
  const entries = Object.entries(modes) as [keyof B2bWriteModes, B2bWriteModes[keyof B2bWriteModes]][];
  const live = entries.filter(([, mode]) => mode === "live");
  const dryRun = entries.filter(([, mode]) => mode === "dry-run");
  const readonly = entries.filter(([, mode]) => mode === "readonly");

  if (readonly.length === 0 && dryRun.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        <span>
          All operations are <strong>live</strong>. Changes apply to production immediately.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
      <div className="flex items-center gap-2 font-medium">
        <FlaskConical className="h-4 w-4 shrink-0" />
        <span>This section is part-way through its production rollout.</span>
      </div>
      <ul className="ml-6 list-disc space-y-1">
        {live.length > 0 && (
          <li>
            <strong>Live</strong> (applies for real): {live.map(([op]) => op).join(", ")}
          </li>
        )}
        {dryRun.length > 0 && (
          <li>
            <strong>Dry run</strong> (diff recorded and emailed, nothing committed):{" "}
            {dryRun.map(([op]) => op).join(", ")}
          </li>
        )}
        {readonly.length > 0 && (
          <li className="flex flex-wrap items-center gap-1">
            <Eye className="h-3.5 w-3.5" />
            <strong>Read-only</strong> (refused): {readonly.map(([op]) => op).join(", ")}
          </li>
        )}
      </ul>
      <Link to="/dashboard/b2b/settings" className="ml-6 inline-block underline">
        Change write modes
      </Link>
    </div>
  );
}
